import React from 'react';
import { beltForScore } from '../../utils/score';
import { useTheme } from '../../contexts/ThemeContext';
import { adjustForTheme } from '../../utils/taskColor';
import DojoMark from './DojoMark';

interface BeltBadgeProps {
  score: number;
  /** Compact for a screen header, larger for a panel. */
  size?: number;
  showLabel?: boolean;
}

/**
 * The rank badge: a ring whose colour is the belt and whose fill is progress to
 * the next one, with the dojo mark tinted to match at its centre.
 *
 * Same information, colours and thresholds as the mobile badge -- both read
 * beltForScore from the ported scoring module -- and now the same construction
 * too. It previously centred a PNG of the mark, which cannot be tinted, so at
 * header size the badge read as a dark smudge rather than as a rank. It is
 * still not a pixel copy: this is drawn for a browser rather than a phone
 * header, so it can afford a label beside it where the phone cannot.
 *
 * The White belt is the case that catches people out. Its colour is #EDE7DA --
 * nearly invisible on a cream ground and nearly invisible as an unfilled track
 * on a dark one -- so the track is drawn from the theme rather than a fixed
 * grey, and the ring gets a hairline when the belt is too pale to hold an edge
 * on its own.
 */
const BeltBadge: React.FC<BeltBadgeProps> = ({ score, size = 40, showLabel = false }) => {
  const { belt, percent } = beltForScore(score);
  const { isDark, colors } = useTheme();

  const stroke = Math.max(3, Math.round(size * 0.09));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, percent));
  const offset = c - (c * pct) / 100;

  const track = isDark ? '#2B3A44' : '#E2DCD0';
  const label = `${belt.name} belt — ${percent}% to next`;

  // The belt colour, nudged until it survives the ground it is drawn on. White
  // is #EDE7DA: on the cream light theme, an untreated ring and an untreated
  // mark are both invisible, and the badge reads as an empty circle -- which is
  // what the phone does here and is worth not copying. adjustForTheme darkens
  // very light colours for light mode and lightens very dark ones for dark
  // mode, and leaves everything in between alone, so Blue and Brown are
  // untouched while White and Black stop vanishing.
  const ink = adjustForTheme(belt.color, isDark);

  return (
    <div className="flex items-center gap-3" title={label}>
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90 block" aria-hidden="true">
          <circle
            cx={size / 2} cy={size / 2} r={r}
            stroke={track} strokeWidth={stroke} fill="none"
          />
          {/* No arc at 0% -- a round cap still paints a dot, which reads as a
              sliver of progress that has not been earned. */}
          {pct > 0 && (
            <circle
              cx={size / 2} cy={size / 2} r={r}
              stroke={ink} strokeWidth={stroke} fill="none"
              strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <DojoMark size={Math.round(size * 0.58)} color={ink} title={label} />
        </div>
      </div>
      {showLabel && (
        <div className="min-w-0">
          <div className="text-sm font-bold truncate" style={{ color: ink }}>
            {belt.name} belt
          </div>
          <div className={`text-xs tabular-nums ${colors.textSecondary}`}>
            {score.toLocaleString()} pts · {percent}% to next
          </div>
        </div>
      )}
    </div>
  );
};

export default BeltBadge;
